import type { SubjectTypePayload } from '@Application/SubjectTypes/Actions/subjectTypesFetched';
import LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';
import SubjectFieldDefinitionView from '@UI/Store/SubjectTypes/Views/SubjectFieldDefinitionView';
import SubjectSpeakerRoleView from '@UI/Store/SubjectTypes/Views/SubjectSpeakerRoleView';
import SubjectTypeWordingView from '@UI/Store/SubjectTypes/Views/SubjectTypeWordingView';

export type SubjectTypeInformationMode = {
	readonly value: 'none' | 'notice' | 'signed_consent';
	readonly label: string;
};

export type SubjectTypeLevel = { readonly value: 'use' | 'supervise'; readonly label: string };

const EXTERNAL_ABOUT = 'external';

/** Immutable View of a dossier type the current user can reach, with their access level. */
class SubjectTypeView {
	readonly id: number;
	readonly key: string;
	readonly wording: SubjectTypeWordingView;
	readonly icon: LabeledValueView;
	readonly color: LabeledValueView;
	readonly informationMode: SubjectTypeInformationMode;
	readonly level: SubjectTypeLevel;
	readonly fields: readonly SubjectFieldDefinitionView[];
	readonly about: readonly LabeledValueView[];
	readonly speakerRoles: readonly SubjectSpeakerRoleView[];

	constructor(payload: SubjectTypePayload) {
		this.id = payload.id;
		this.key = payload.key;
		this.wording = new SubjectTypeWordingView(payload.wording);
		this.icon = new LabeledValueView(payload.icon);
		this.color = new LabeledValueView(payload.color);
		this.informationMode = {
			value: payload.informationMode.value as SubjectTypeInformationMode['value'],
			label: payload.informationMode.label,
		};
		this.level = {
			value: payload.level.value as SubjectTypeLevel['value'],
			label: payload.level.label,
		};
		this.fields = (payload.fields ?? []).map((field) => new SubjectFieldDefinitionView(field));
		this.about = (payload.about ?? []).map((kind) => new LabeledValueView(kind));
		this.speakerRoles = (payload.speakerRoles ?? []).map((role) => new SubjectSpeakerRoleView(role));

		Object.freeze(this);
	}

	get hasAbout(): boolean {
		return this.about.length > 0;
	}

	get allowsExternal(): boolean {
		return this.about.some((kind) => kind.value === EXTERNAL_ABOUT);
	}

	/** The populations the user can search: every `about` kind but `external`, which has no directory. */
	get searchablePopulations(): readonly LabeledValueView[] {
		return this.about.filter((kind) => kind.value !== EXTERNAL_ABOUT);
	}
}

export default SubjectTypeView;
